import React, { useEffect, useRef, useState } from "react";
import { IoIosSearch, IoIosCloseCircle } from "react-icons/io";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { EventColumns, EventColumnsHeaders, FTPColumns, FTPColumnsHeaders } from '../../SiteSummary/constants'
import { EventTable } from "../Table/EventTable";
import { v4 as uuidv4 } from 'uuid';
import { Popover, IconButton, Box, Button, Typography } from '@mui/material';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs, { Dayjs } from 'dayjs';
import { FTPTable } from "../Table/FTPTable";



const calendarStyles = {
    backgroundColor: 'var(--Darkmode-700, #131F2E)',
    '& .MuiPickersDay-root': { color: 'white' },
    '& .MuiPickersDay-root:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: 'white !important',
    },
    '& .Mui-selected': {
        color: 'white !important',
        backgroundColor: 'rgba(0, 148, 189, 0.8) !important',
    },
    '& .Mui-selected:hover': {
        backgroundColor: 'rgba(0, 148, 189, 1) !important',
        color: 'white !important',
    },
    '& .MuiPickersDay-root.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.5)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        pointerEvents: 'none',
        borderRadius: '50%',
    },
    '& .MuiDayCalendar-header': { color: 'white' },
    '& .MuiPickersCalendarHeader-label': { color: 'white' },
    '& .MuiIconButton-root': { color: 'white' },
    '& .MuiTypography-root': { color: 'white' },
    '& .MuiDayPicker-weekDayLabel': { color: 'white' },
    '& .MuiIconButton-root.Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.3)',
        pointerEvents: 'none',
    },
};

type SetTabPageFunction = React.Dispatch<React.SetStateAction<string>>
interface SearchProps {
    tableData: any[];
    setTabDivHeight: SetTabPageFunction;
    addedRows: any[];
    setAddedRows: React.Dispatch<React.SetStateAction<any[]>>;
    isToggle: boolean;
    selectedOption: string;
    setSelectedOption: React.Dispatch<React.SetStateAction<string>>
    selectedRange: {
        start: Dayjs | null;
        end: Dayjs | null;
    };
    setSelectedRange: React.Dispatch<React.SetStateAction<{
        start: Dayjs | null;
        end: Dayjs | null;
    }>>;
    setClickApply: React.Dispatch<React.SetStateAction<boolean>>;
    showFTPDataMessage: boolean;
    onHandleRefresh: () => Promise<void>;
    assetId: string;
}


const FTPFormTable: React.FC<SearchProps> = ({ tableData,assetId, setTabDivHeight, addedRows, setAddedRows, isToggle, setSelectedOption, onHandleRefresh, setSelectedRange, setClickApply, showFTPDataMessage }) => {
    const [page, setPage] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [filterData, setFilterData] = useState<any[]>(tableData);
    const [currentPageNumber, setCurrentPageNumber] = useState(1)
    const [isMobile, setIsMobile] = useState(window.innerWidth < 620); // Assuming width less than 768px is considered mobile
    const inputRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(dayjs());
    const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(dayjs());
    const open = Boolean(anchorEl);
    const id = open ? 'date-range-popover' : undefined;
    const [handleClickApply, setHandleClickApply] = useState(false);

    useEffect(() => {
        setTabDivHeight("1250px");
        setHandleClickApply(false)
    }, []);


    useEffect(() => {
        let timeoutId;

        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setIsMobile(window.innerWidth < 620); // Update state based on window width             
            }, 100); // Adjust the debounce interval as needed
        };

        window.addEventListener('resize', handleResize); // Add event listener for window resize

        return () => {
            window.removeEventListener('resize', handleResize); // Remove event listener on component unmount
        };
    }, [window.innerWidth]);


    useEffect(() => {
        setFilterData(tableData)
    }, [tableData])

    const handleAddRow = () => {
        const currentDate = new Date().toLocaleString('en-US', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,  // 24-hour format
        });


        const newRow = {
            eventId: uuidv4(),
            date: currentDate,
            type: "User Comments",
            source: localStorage.getItem("jwtUserEmail"),
            comment: "",
            change: "NA",
            added: true
        };

        // Add new row to the top of addedRows array
        setAddedRows([newRow, ...addedRows]);
        setFilterData([newRow, ...filterData])
    };


    const handleCalendarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setSelectedOption("Custom");  // Make "Custom" active when clicked
    };

    const handleClick = (label: string, event?: React.MouseEvent<HTMLElement>) => {
        if (label === "Custom") {
            if (event) handleCalendarClick(event);
        } else {
            setSelectedRange({ start: null, end: null })
            setTempStartDate(dayjs());
            setTempEndDate(dayjs());
            setSelectedOption(label);
            setHandleClickApply(false)
        }
        setPage(1)
    };

    const handleCancel = () => {
        if (handleClickApply) {
            setAnchorEl(null);
            return;
        }
        setTempStartDate(dayjs());
        setTempEndDate(dayjs());
        setAnchorEl(null);
    };

    const handleApply = () => {
        setClickApply(true)
        setHandleClickApply(true)
        setSelectedRange({ start: tempStartDate, end: tempEndDate });
        setTempStartDate(tempStartDate);
        setTempEndDate(tempEndDate);
        setAnchorEl(null);
    };
    const handleValueChange = (searchTerm: string) => {

        setSearchValue(searchTerm);
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const finalfilteredData = tableData?.filter((item) => {
            return Object.values(item).some((value) => {
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(lowerCaseSearchTerm);
                }
                return false;
            });
        });
        setPage(1)
        if (searchTerm === "") {
            setFilterData([...addedRows, ...tableData]);
        }
        else {
            setFilterData(finalfilteredData);
        }
    };


    const removeSearchText = () => {
        setSearchValue("");
        setFilterData([...addedRows, ...tableData]);
    };

    // Focus the input box initially and when searchValue is cleared
    useEffect(() => {
        handleFocus()
    }, [searchValue]);

    const handleFocus = () => {
        // Delay to avoid conflicts with other focus events        
        inputRef.current?.focus();
    };


    return (
        <>
            <FTPTable assetId = {assetId} data={filterData} columns={FTPColumns} columnHeaders={FTPColumnsHeaders} defaultSortColumn="fileName" defaultSortOrder="desc" pagniation={true} page={page} setPage={setPage} searchValue={searchValue} addedRows={addedRows} setAddedRows={setAddedRows} handleAddRow={handleAddRow} setFilterData={setFilterData} isToggle={isToggle} showFTPDataMessage={showFTPDataMessage} onHandleRefresh = {onHandleRefresh} />
        </>

    );
};

export default FTPFormTable;
